import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowUpRight } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const HANDMADE_INFO =
    "All cakes are made to order, we require approximately 4 days notice. There may be slight variances in colour and decorations, but will always do our best to match it exactly as illustrated.";

const DELIVERY_INFO =
    "If delivery is needed this will be an additional cost, mention it above and will send you a quote depending on location.";

export default function VintageHeartInquiryForm({ product }) {
    const sizeOptions = product?.size_options || [];
    const [form, setForm] = useState({
        event_date: "",
        name: "",
        phone: "",
        flavour_choice: "",
        message: "",
    });
    const [sizeLabel, setSizeLabel] = useState("");
    const [cakeWriting, setCakeWriting] = useState("");
    const [fulfillment, setFulfillment] = useState("");
    const [handmadeOpen, setHandmadeOpen] = useState(false);
    const [dimensionsOpen, setDimensionsOpen] = useState(false);
    const [deliveryOpen, setDeliveryOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const selectedSize = sizeOptions.find((s) => s.label === sizeLabel);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (!sizeLabel) { toast.error("Please choose a size."); return; }
        if (!fulfillment) { toast.error("Please choose pick up or delivery."); return; }
        setSubmitting(true);
        try {
            await api.post("/inquiries", {
                ...form,
                product_slug: product?.slug,
                product_name: product?.name,
                size_label: sizeLabel,
                size_price: selectedSize?.price ?? null,
                fulfillment_type: fulfillment,
                message: cakeWriting ? `Writing on cake: ${cakeWriting}\n\n${form.message}` : form.message,
            });
            toast.success("Thank you! Your enquiry has been sent. We'll be in touch shortly.");
            setForm({ event_date: "", name: "", phone: "", flavour_choice: "", message: "" });
            setSizeLabel("");
            setCakeWriting("");
            setFulfillment("");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="vh-inquiry-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Date *</label>
                    <input
                        type="date"
                        name="event_date"
                        required
                        value={form.event_date}
                        onChange={onChange}
                        className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                        data-testid="vh-date"
                    />
                </div>
                <div>
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Name *</label>
                    <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={onChange}
                        className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                        data-testid="vh-name"
                    />
                </div>
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Contact Number *</label>
                <input
                    type="tel"
                    name="phone"
                    required
                    value={form.phone}
                    onChange={onChange}
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                    data-testid="vh-phone"
                />
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Size</label>
                <select
                    required
                    value={sizeLabel}
                    onChange={(e) => setSizeLabel(e.target.value)}
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                    data-testid="vh-size"
                >
                    <option value="">Select a size…</option>
                    {sizeOptions.map((s) => (
                        <option key={s.label} value={s.label}>
                            {s.label} ({s.dessert_serves} Dessert, {s.coffee_serves} Coffee){s.price > 0 ? ` — $${Number(s.price).toFixed(0)}` : ""}
                        </option>
                    ))}
                </select>
                {selectedSize && selectedSize.price > 0 && (
                    <p className="text-blush text-lg font-medium mt-2" data-testid="vh-size-price">
                        ${Number(selectedSize.price).toFixed(0)}
                    </p>
                )}
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft">Flavour</label>
                    <Link
                        to="/flavours"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-blush hover:text-[#C4007A] transition-colors shrink-0"
                        data-testid="vh-flavour-list-link"
                    >
                        View flavours list <ArrowUpRight size={11} />
                    </Link>
                </div>
                <input
                    type="text"
                    name="flavour_choice"
                    value={form.flavour_choice}
                    onChange={onChange}
                    placeholder="e.g. Vanilla, Chocolate…"
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                    data-testid="vh-flavour-choice"
                />
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Writing on cake</label>
                <input
                    type="text"
                    value={cakeWriting}
                    onChange={(e) => setCakeWriting(e.target.value)}
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                    data-testid="vh-writing"
                />
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Message</label>
                <textarea
                    name="message"
                    rows={3}
                    value={form.message}
                    onChange={onChange}
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink resize-none"
                    data-testid="vh-message"
                />
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Delivery / Pick up</label>
                <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={fulfillment === "Pickup"}
                            onChange={() => setFulfillment(fulfillment === "Pickup" ? "" : "Pickup")}
                            data-testid="vh-pickup"
                        />
                        <span className="text-sm text-ink">Pick up</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={fulfillment === "Delivery"}
                            onChange={() => setFulfillment(fulfillment === "Delivery" ? "" : "Delivery")}
                            data-testid="vh-delivery"
                        />
                        <span className="text-sm text-ink">Delivery</span>
                    </label>
                </div>
                <p className="text-[11px] text-ink-soft italic mt-1.5">Delivery is additional, please refer below.</p>
            </div>

            <div className="border-t border-[#EAE3DE] pt-4">
                <button
                    type="button"
                    onClick={() => setHandmadeOpen((v) => !v)}
                    className="flex items-center justify-between w-full text-left"
                    data-testid="vh-handmade-toggle"
                >
                    <span className="text-[11px] tracking-[0.25em] uppercase text-ink font-medium">Handmade</span>
                    <span className="w-7 h-7 border border-ink flex items-center justify-center shrink-0">
                        <ChevronDown size={14} className={`transition-transform ${handmadeOpen ? "rotate-180" : ""}`} />
                    </span>
                </button>
                {handmadeOpen && (
                    <p className="mt-4 text-xs text-ink-soft leading-relaxed" data-testid="vh-handmade-content">
                        {HANDMADE_INFO}
                    </p>
                )}
            </div>

            <div className="border-t border-[#EAE3DE] pt-4">
                <button
                    type="button"
                    onClick={() => setDimensionsOpen((v) => !v)}
                    className="flex items-center justify-between w-full text-left"
                    data-testid="vh-dimensions-toggle"
                >
                    <span className="text-[11px] tracking-[0.25em] uppercase text-ink font-medium">Size / Dimensions</span>
                    <span className="w-7 h-7 border border-ink flex items-center justify-center shrink-0">
                        <ChevronDown size={14} className={`transition-transform ${dimensionsOpen ? "rotate-180" : ""}`} />
                    </span>
                </button>
                {dimensionsOpen && (
                    <div className="mt-4 space-y-2" data-testid="vh-dimensions-content">
                        {sizeOptions.map((s) => (
                            <p key={s.label} className="text-xs text-ink-soft leading-relaxed">
                                <span className="text-ink font-medium">{s.label}</span> — {s.dimensions} — {s.dessert_serves} dessert / {s.coffee_serves} coffee
                            </p>
                        ))}
                    </div>
                )}
            </div>

            <div className="border-t border-[#EAE3DE] pt-4">
                <button
                    type="button"
                    onClick={() => setDeliveryOpen((v) => !v)}
                    className="flex items-center justify-between w-full text-left"
                    data-testid="vh-delivery-info-toggle"
                >
                    <span className="text-[11px] tracking-[0.25em] uppercase text-ink font-medium">Delivery</span>
                    <span className="w-7 h-7 border border-ink flex items-center justify-center shrink-0">
                        <ChevronDown size={14} className={`transition-transform ${deliveryOpen ? "rotate-180" : ""}`} />
                    </span>
                </button>
                {deliveryOpen && (
                    <p className="mt-4 text-xs text-ink-soft leading-relaxed" data-testid="vh-delivery-info-content">
                        {DELIVERY_INFO}
                    </p>
                )}
            </div>

            <button
                type="submit"
                disabled={submitting}
                className="btn-pink w-full disabled:opacity-60"
                data-testid="vh-inquiry-submit"
            >
                {submitting ? "Sending…" : "Send Enquiry"}
            </button>
        </form>
    );
}
